"use client";

import { useSDUIOnClickHandler } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/ActionHandler/useSDUIOnClickHandler";
import {
  getContextToken,
  getProductTileImpressionContext,
  getProductTileContainer,
  getProductTileClickImpressionContainerName,
  getProductTileScreenContext,
  getProductTileScreenContainerContext,
} from "@faire/retailer-visitor-shared/components/SDUI/__internal__/SDUIProductTileImpressionProvider";
import {
  ActionLoggingEventParams,
  ComponentLoggingEventParams,
} from "@faire/retailer-visitor-shared/components/SDUI/__internal__/types";
import {
  useGenerateBrandUrlForSDUIProductTileHandler,
  useGenerateProductLinkPropsForSDUIProductTileHandler,
} from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Tile/__internal__/ProductTile/SDUIProductTileUrlHandler";
import { ITrackingProps } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ProductTile/types";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import {
  SignUpElement,
  SignUpType,
  useTrackSignupModalOpenClick,
} from "@faire/retailer-visitor-shared/lib/popSignUp";
import { useSearchPageIsFromDiscoverURL } from "@faire/retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useSearchPageIsFromDiscoverURL";
import { trackSeoSearchProductListBrandLinkRefsClick } from "@faire/web-api--source/events/seo/click/searchProductListBrandLinkRefs";
import { trackSeoSearchProductListProductLinkRefsClick } from "@faire/web-api--source/events/seo/click/searchProductListProductLinkRefs";
import { trackSignupModalOpenDiscoverProductTileClick } from "@faire/web-api--source/events/signup/click/modalOpenDiscoverProductTile";
import { trackSignupModalOpenSearchProductTileClick } from "@faire/web-api--source/events/signup/click/modalOpenSearchProductTile";
import { DisplayPageType } from "@faire/web-api--source/indigofair/data/DisplayPageType";
import { IAction } from "@faire/web-api--source/indigofair/data/IAction";
import { getLocation } from "@faire/web/common/globals/getLocation";
import { trackEvent } from "@faire/web/common/trackEvent";
import kebabCase from "lodash/kebabCase";
import qs from "qs";
import { useMemo } from "react";

interface TileInteractionsProps {
  on_click: IAction[];
  trackingProps: ITrackingProps;
  brandToken: string;
  loggingData: ActionLoggingEventParams;
}

export const useTileInteractions = ({
  on_click,
  trackingProps,
  brandToken,
  loggingData,
}: TileInteractionsProps) => {
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const isFromDiscoverURL = useSearchPageIsFromDiscoverURL();
  const location = getLocation();
  const recordSignUpModalOpenCickEvent = useTrackSignupModalOpenClick();
  const generateProductLinkProps =
    useGenerateProductLinkPropsForSDUIProductTileHandler(
      trackingProps.productToken,
      trackingProps.selectedProductOptionToken,
      trackingProps.impressionId,
      loggingData.view_display_page
    );
  const generateBrandUrl = useGenerateBrandUrlForSDUIProductTileHandler(
    trackingProps.productToken,
    trackingProps.brandToken,
    trackingProps.impressionId,
    loggingData.view_display_page,
    isLoggedInRetailer
  );

  const brandClickLoggingData = useMemo(() => {
    return {
      ...loggingData,
      action_instance_identifier: brandToken,
    };
  }, [loggingData, brandToken]);

  const onBrandClickHandler = useSDUIOnClickHandler(
    on_click,
    brandClickLoggingData,
    { logSDUIClickEvent: true, logLegacyClickEvent: true }
  );
  const isCategoryPage =
    loggingData.view_display_page === DisplayPageType.CATEGORY_PAGE;
  const onProductClickHandler = useSDUIOnClickHandler(
    on_click,
    {
      ...loggingData,
      action_instance_identifier: isCategoryPage
        ? loggingData.action_instance_identifier
        : undefined,
      section_type: isCategoryPage
        ? (loggingData?.section_type ?? "")
        : undefined,
      section_position: isCategoryPage
        ? loggingData?.section_position
        : undefined,
      action_content: isCategoryPage ? loggingData.action_content : undefined,
      action_positions: isCategoryPage
        ? loggingData.action_positions
        : undefined,
    },
    {
      logSDUIClickEvent: true,
      logLegacyClickEvent: true,
    }
  );

  const onBrandClick = () => {
    onBrandClickHandler();
    if (loggingData.view_display_page === DisplayPageType.SEARCH_PAGE) {
      const productSearchString = qs
        .parse(location?.search ?? "")
        .q?.toString();
      trackEvent(
        `/search-click/${kebabCase(
          productSearchString
        )}/product/${trackingProps.productToken}/brand/${brandToken}`
      );
      trackSeoSearchProductListBrandLinkRefsClick(
        brandToken ?? "",
        productSearchString ?? ""
      );
    }
  };

  const onProductClick = () => {
    onProductClickHandler();
    if (loggingData.view_display_page === DisplayPageType.SEARCH_PAGE) {
      const productSearchString = qs
        .parse(location?.search ?? "")
        .q?.toString();
      trackEvent(
        `/search-click/${kebabCase(productSearchString)}/product/${
          trackingProps.productToken
        }`
      );
      trackSeoSearchProductListProductLinkRefsClick(
        trackingProps.productToken ?? "",
        productSearchString ?? ""
      );
      if (!isLoggedInRetailer) {
        recordSignUpModalOpenCickEvent(
          SignUpType.PRODUCT,
          SignUpElement.PRODUCT_GRID,
          `logged_out_product_tile`
        );
        if (isFromDiscoverURL) {
          trackSignupModalOpenDiscoverProductTileClick();
        } else {
          trackSignupModalOpenSearchProductTileClick();
        }
      }
    }
  };

  return {
    tileActions: {
      onProductClick: () => onProductClick(),
      onBrandClick: () => onBrandClick(),
    },
    props: {
      productLinkProps: generateProductLinkProps(),
      brandLinkProps: {
        brandUrl: generateBrandUrl() ?? "",
        brandToken: brandToken,
      },
    },
  };
};

export const useLoggingContext = (loggingData: ComponentLoggingEventParams) => {
  const productTileContainer = getProductTileContainer(loggingData);
  const context = getProductTileImpressionContext(
    productTileContainer,
    loggingData
  );
  const containerName = getProductTileClickImpressionContainerName(loggingData);
  const requestId = loggingData.request_id;
  const screenContext = getProductTileScreenContext(loggingData);
  const containerContext = getProductTileScreenContainerContext(loggingData);
  const contextToken = getContextToken(loggingData);

  return {
    impressionProviderProps: {
      context,
      containerName,
      requestId,
      screenContext,
      containerContext,
      contextToken,
      trackedLinkContainerName: containerName,
    },
  };
};
